/**
 * ActionQueue - Offline-first persistence for cantera stats
 * Uses IndexedDB to store game state and actions so data survives
 * browser crashes, tab closes, or network failures during LIVE games.
 */

const DB_NAME = 'cantera_stats';
const DB_VERSION = 1;
const STORE_GAME_STATE = 'gameState';
const STORE_PENDING_ACTIONS = 'pendingActions';

let dbInstance = null;

function openDB() {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_GAME_STATE)) {
        db.createObjectStore(STORE_GAME_STATE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_PENDING_ACTIONS)) {
        const store = db.createObjectStore(STORE_PENDING_ACTIONS, { keyPath: 'id', autoIncrement: true });
        store.createIndex('gameKey', 'gameKey', { unique: false });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onerror = (event) => {
      console.error('IndexedDB open error:', event.target.error);
      reject(event.target.error);
    };
  });
}

function doTransaction(storeName, mode, callback) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      const result = callback(store);

      tx.oncomplete = () => resolve(result._result);
      tx.onerror = (event) => reject(event.target.error);

      // For get/getAll requests, capture the result
      if (result instanceof IDBRequest) {
        result.onsuccess = () => { result._result = result.result; };
      }
    });
  });
}

/**
 * Build a unique key for the current game session
 */
function buildGameKey(groupeId, gameId) {
  return `${groupeId}_${gameId || 'new'}`;
}

const actionQueue = {
  /**
   * Save the full game state (actions, score, periods, players, etc.)
   * Called periodically and on every action in LIVE mode.
   */
  async saveGameState(groupeId, state) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_GAME_STATE, 'readwrite');
      const store = tx.objectStore(STORE_GAME_STATE);

      const record = {
        id: `game_${groupeId}`,
        groupeId,
        timestamp: Date.now(),
        state: {
          statsMode: state.statsMode,
          currentGame: state.currentGame,
          actionsLog: state.actionsLog,
          activePlayers: state.activePlayers,
          playerTimeTracking: state.playerTimeTracking,
          currentPeriod: state.currentPeriod,
          periodSeconds: state.periodSeconds,
          puntuacionLocal: state.puntuacionLocal,
          puntuacionVisitante: state.puntuacionVisitante,
          videoUrl: state.videoUrl,
          periodOffsets: state.periodOffsets,
          selectedVideoGame: state.selectedVideoGame,
          gameInProgress: state.gameInProgress,
        }
      };

      store.put(record);
      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      console.error('Error saving game state to IndexedDB:', error);
    }
  },

  /**
   * Check if there is unsaved game state for a group
   */
  async getGameState(groupeId) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_GAME_STATE, 'readonly');
      const store = tx.objectStore(STORE_GAME_STATE);
      const request = store.get(`game_${groupeId}`);

      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const record = request.result;
          if (record && record.state && record.state.actionsLog && record.state.actionsLog.length > 0) {
            resolve(record);
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error reading game state from IndexedDB:', error);
      return null;
    }
  },

  /**
   * Clear game state after successful save
   */
  async clearGameState(groupeId) {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_GAME_STATE, 'readwrite');
      const store = tx.objectStore(STORE_GAME_STATE);
      store.delete(`game_${groupeId}`);
      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      console.error('Error clearing game state from IndexedDB:', error);
    }
  },

  /**
   * Get count of pending actions (for badge display)
   */
  async getPendingCount(groupeId) {
    try {
      const state = await this.getGameState(groupeId);
      if (!state) return 0;
      return state.state.actionsLog?.length || 0;
    } catch (error) {
      return 0;
    }
  },

  /**
   * Get the age of the saved state in minutes
   */
  async getStateAge(groupeId) {
    try {
      const state = await this.getGameState(groupeId);
      if (!state) return null;
      return Math.round((Date.now() - state.timestamp) / 60000);
    } catch (error) {
      return null;
    }
  }
};

export default actionQueue;
